import * as base64url from 'universal-base64url';
import { encode } from 'universal-base64url';
import type { CMSContentDTO } from '@/services/generated/ECAPICmsDTOs';
import type { SummaryDTO } from '@/services/generated/ECAPIProductRatingsDTOs';
import type {
  HierarchicalCategoryDTO,
  ProductCategoryAllDetailsDTO,
  PricesResponseDTO,
  ProductDTO
} from '@/services/generated/ECAPIProductsDTOs';
import type {
  NumberFormatsDTO,
  StoreDTO
} from '@/services/generated/ECAPIStoresDTOs';
import type { SubscriptionsConfigurationsDTO } from '@/services/generated/ECAPISubscriptionsDTOs';
import type {
  BasicInfoResponseDTO,
  SpecificProductsResponseDTO
} from '../../services/generated/ECAPICustomersDTOs.ts';
import type {
  CartLineRequestDTO,
  UnquotedCartResponseDTO
} from '../../services/generated/ECAPICartDTOs.ts';
import getCredentialsOptions from '@/utils/getCredentialsOptions.tsx';

// Legacy ECAPI endpoint type
export type UserGroupsDTO = {
  userGroups: string[];
};

export async function productsByCategories(
  market: string,
  channel: string,
  locale: string,
  superCategory: string,
  customerState: 'NONE' | 'ANONYMOUS',
  cacheBurst?: boolean
): Promise<ProductCategoryAllDetailsDTO[]> {
  const result = await api(
    `/products/v2/${market}/${channel}/productsByCategories?language=${locale}&superCategory=${superCategory}&usageIntent=standard-order&allDetails=true&frontend=Responsive&customerState=${customerState}`,
    cacheBurst
  );

  return result;
}

export async function productById(
  market: string,
  channel: string,
  locale: string,
  id: string,
  cacheBurst?: boolean
): Promise<ProductDTO> {
  const encodedId = encode(id);
  const result = await api(
    `/products/v2/${market}/${channel}/${encodedId}?language=${locale}`,
    cacheBurst
  );

  return result;
}

export async function stores(
  market: string,
  channel: string,
  cacheBurst?: boolean
): Promise<StoreDTO> {
  const result = await api(`/stores/v5/${market}/${channel}`, cacheBurst);

  return result;
}

export async function numberFormats(
  market: string,
  channel: string,
  locale: string,
  cacheBurst?: boolean
): Promise<NumberFormatsDTO> {
  const result = await api(
    `/stores/v5/${market}/${channel}/numberFormats?language=${locale}`,
    cacheBurst
  );

  return result;
}

export async function ratingSummary(
  market: string,
  locale: string,
  productId: string,
  cacheBurst?: boolean
): Promise<SummaryDTO> {
  const encodedId = base64url.encode(productId);
  const result = await api(
    `/rating/v1/${market}/${locale}/${encodedId}/summary?platform=desktop`,
    cacheBurst
  );
  return result;
}

export async function subscriptions(
  market: string,
  locale: string,
  cacheBurst?: boolean
): Promise<SubscriptionsConfigurationsDTO> {
  // b2c only
  const result = await api(
    `/subscription/v4/${market}/config?language=${locale}&ignoreFrontendFilter=true`,
    cacheBurst
  );
  return result;
}

export async function productPrices(
  market: string,
  channel: string,
  taxSystem?: string,
  tariff?: string,
  currency?: string,
  cacheBurst?: boolean
): Promise<PricesResponseDTO> {
  const urlObj = new URL(
    `/products/v2/${market}/${channel}/prices`,
    window.location.href
  );

  if (taxSystem) {
    urlObj.searchParams.set('taxSystemCode', taxSystem);
  }

  if (tariff) {
    urlObj.searchParams.set('tariffCode', tariff);
  }

  if (currency) {
    urlObj.searchParams.set('currencyCode', currency);
  }

  const result = await api(urlObj.pathname + urlObj.search, cacheBurst);
  return result;
}

export async function cms(
  market: string,
  channel: string,
  contentId: string,
  locale?: string,
  cacheBurst?: boolean
): Promise<CMSContentDTO> {
  const result = await api(
    `/cms/v1/${market}/${channel}/${contentId}${
      locale ? `?language=${locale}` : ''
    }`,
    cacheBurst
  );

  return result;
}

export async function productCategories(
  market: string,
  channel: string,
  locale: string,
  cacheBurst?: boolean
): Promise<HierarchicalCategoryDTO[]> {
  const result = await api(
    `/products/v2/${market}/${channel}/categories?language=${locale}&frontend=Responsive`,
    cacheBurst
  );

  return result;
}

export async function stocks(
  market: string,
  channel: string,
  cacheBurst: boolean = false
): Promise<{ productId: string; isInStock: boolean }[]> {
  const result = await api(`/stocks/v1/${market}/${channel}`, cacheBurst);

  return result;
}

export async function customerInfo(
  market: string,
  channel: string,
  cacheBurst: boolean = false
): Promise<BasicInfoResponseDTO> {
  const result = await api(
    `/customers/v7/${market}/${channel}/me/basic-info`,
    cacheBurst
  );

  return result;
}

export async function customerSpecificProducts(
  market: string,
  channel: string,
  cacheBurst: boolean = false
): Promise<SpecificProductsResponseDTO> {
  const result = await api(
    `/customers/v7/${market}/${channel}/me/specific-products`,
    cacheBurst
  );

  return result;
}

export async function customerSelections(
  market: string,
  locale: string,
  cacheBurst: boolean = false
): Promise<UserGroupsDTO> {
  const result = await api(
    `/${market}/${locale}/ecapi/1/user/user-groups`,
    cacheBurst,
    '' // On Root, not /ecapi
  );

  return result;
}

export async function getFavoriteCart(
  market: string,
  channel: string,
  cacheBurst: boolean = false
): Promise<UnquotedCartResponseDTO> {
  const { carts } = await api(
    `/customers/v7/${market}/${channel}/me/carts`,
    cacheBurst
  );

  const favoriteCart = carts.find(
    (cart: { role: string }) => cart.role === 'FAVORITE'
  );

  if (!favoriteCart) {
    return createFavoriteCart(market, channel, []);
  }

  // Existing Favorite Cart
  const cartDetails = await api(
    `/carts/v4/${market}/${channel}/${favoriteCart.id}?frontend=Classic`,
    cacheBurst
  );

  return cartDetails;
}

export async function createFavoriteCart(
  market: string,
  channel: string,
  lines: CartLineRequestDTO[]
): Promise<UnquotedCartResponseDTO> {
  // New Cart Creation
  const newCart = await fetch(
    `/ecapi/carts/v4/${market}/${channel}/me/unquoted?frontend=Classic&cartRole=FAVORITE`,
    {
      method: 'POST',
      body: JSON.stringify({ lines, nextOrders: [], giftCards: [] }),
      ...getCredentialsOptions()
    }
  ).then((res) => res.json());

  // Instantiate the new favorite cart
  await fetch(`/ecapi/customers/v7/${market}/${channel}/me/carts`, {
    method: 'POST',
    body: JSON.stringify({ id: newCart.id, role: 'FAVORITE' }),
    ...getCredentialsOptions()
  });

  return newCart;
}

export async function setFavoriteProducts(
  market: string,
  channel: string,
  cartId: string | undefined,
  lines: CartLineRequestDTO[]
): Promise<{ id: string; lines: CartLineRequestDTO[] }> {
  if (!cartId) {
    const newCart = await createFavoriteCart(market, channel, lines);
    return newCart;
  }

  const updatedCart = await fetch(
    `/ecapi/carts/v4/${market}/${channel}/${cartId}/unquoted?frontend=Classic`,
    {
      method: 'PUT',
      body: JSON.stringify({ lines }),
      ...getCredentialsOptions()
    }
  ).then((res) => res.json());

  return updatedCart;
}

async function api(
  url: string,
  cacheBurst: boolean = false,
  urlPrefix = '/ecapi'
): Promise<any> {
  const fullUrl = `${urlPrefix}${url}`;
  const urlObj = new URL(fullUrl, globalThis.location.href);

  if (cacheBurst) {
    urlObj.searchParams.append('ts', Date.now().toString()); // Cache burst
  }

  const response = await fetch(urlObj.href, getCredentialsOptions());

  if (response.status === 404) {
    return {};
  }

  if (!response.ok) {
    const htmlErrorResponse = await response.text();

    throw new Error(
      `Failed to fetch ${urlObj.href} - ${response.status}:${response.statusText}`,
      {
        cause: htmlErrorResponse ? new Error(htmlErrorResponse) : undefined
      }
    );
  }

  const value = await response.json();
  return value;
}
